/**
 * Tenant context: resolves the active tenant from the URL and manages org email identity.
 *
 * URL patterns:
 *   /~/...          → personal tenant (user.personalTenantId)
 *   /t/:slug/...    → tenant by slug (or ID for now)
 *
 * All API requests include X-Tenant-Id via the getter registered here.
 *
 * Active org-email is server-side state (per session, per tenant) — see
 * features/auth + GET/POST /api/auth/active-email{,s}. The client hydrates
 * the map from the server on mount and updates it via POST when the user
 * switches identity. We still expose the active email through the api-client
 * X-Org-Email getter so HTTP requests carry it (the server uses it to set the
 * stored selection on first call). WebSockets read from the same server-side
 * store (browsers can't set custom WS headers).
 *
 * No localStorage: the source of truth lives on the server.
 */
import { createContext, useContext, useEffect, useLayoutEffect, useReducer, useRef, useMemo, useState, useCallback, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { setActiveTenantGetter, setActiveOrgEmailGetter, request, lastActingOrgEmail } from '@papan/api-client'
import { useAuth } from './useAuth'
import { playIdentitySwitch, identityCardFor } from '@papan/core-ui/ui'
import { landingPath } from '../lib/landing'
import { nextEntry, type ShellKind } from '../lib/identityShell'
import { endSession, sessionContextOf, sessionContextChanged, sessionContextMoveAccountedFor } from '@papan/core-ui/ui/sessionEnded'

interface TenantContextValue {
  /** The resolved active tenant ID (from URL or personal fallback) */
  tenantId: string
  /** Whether the current view is the personal tenant (via /~/) */
  isPersonal: boolean
  /** The slug from the URL (if using /t/:slug/) */
  tenantSlug: string | null
  /** Active org email identity for this tenant */
  activeOrgEmail: string | undefined
  /** Set the org email identity — POSTs to /api/auth/active-email then updates state. */
  setActiveOrgEmail: (email: string | undefined) => Promise<void>
  /** Atomic tenant switch: persists active email server-side, updates refs, then navigates. */
  /** `to`: where to land instead of the home surface (a full path, e.g. the bell's "Edit in <org>" → that org's
   *  Settings → Links). */
  switchTenant: (targetId: string, orgEmail?: string, opts?: { to?: string }) => Promise<void>
  /**
   * #1484 (a PDApps tile run from another org): make `email` this session's identity
   * for `targetId` ONLY IF none is chosen yet. Never switches THIS tab — no refs, no
   * session end; the caller then loads the target fresh, which reads the choice like
   * any other. Resolves false when the server refused (then nothing changed).
   */
  chooseIdentityIfUnset: (targetId: string, email: string) => Promise<boolean>
  /** True when refs match React state — safe for effects to fire API calls */
  isReady: boolean
  /**
   * True once the server-side active-emails map has been fetched. Consumers
   * that auto-pick a default identity must gate on this — without it, the
   * default-pick effect can fire before hydration and clobber the
   * server-stored selection (e.g. one set explicitly via the org switcher
   * submenu, an e2e helper, or a deep link).
   */
  orgEmailsHydrated: boolean
  /**
   * Has the user chosen a tenant during THIS page load?
   *
   * The identity picker (a sign-in disambiguation) reads this to suppress
   * itself once a choice has been made. It lives here, not in a shell, because
   * every switch funnels through `switchTenant` regardless of which surface
   * triggered it — the picker, the desktop avatar menu, or the phone launcher —
   * and a flag local to one surface cannot see the others. A full-tenant user
   * who picked an org and then returned to Personal via the avatar menu was
   * ambushed by the picker again precisely because that second switch bypassed
   * the shell's own state.
   *
   * `switchTenant` sets it only on the SUCCESS path (after the active-email POST
   * resolves), so a refused switch never marks a choice and the picker stays as
   * recourse. It resets on a full reload — the provider remounts — which is the
   * "once per page load" contract the picker is meant to honour.
   */
  tenantChosen: boolean
  /** Mark a tenant chosen WITHOUT switching — for picking the identity you are
   *  already on, which navigates nowhere but must still dismiss the picker. */
  markTenantChosen: () => void
  /**
   * Where this page load ENTERED the app, for the identity picker (#1253): a
   * `/~/…` or `/t/…` deep link names the destination, so it counts as a choice.
   * `''` = no destination. Before anything is captured, the live path.
   * See `nextEntry` (lib/identityShell.ts) for the rules.
   */
  entryPath: string
  /** `EntryCapture` (lib/EntryCapture.tsx): the routed tree committed here. */
  captureEntry: (path: string, shell: ShellKind) => void
  /** `EntryUnroutedMark`: a shell's catch-all caught this path. */
  markEntryUnrouted: (path: string, shell: ShellKind) => void
}

const TenantContext = createContext<TenantContextValue | null>(null)

/** #1316: does choosing `next` change who requests act as in this tenant? From NO stored identity, the requests so far
 *  carried no `x-org-email` and the server acted as its own default — the identity it echoes (`lastActingOrgEmail`).
 *  Naming that same identity (the first-visit auto-pick, the usual case) is no switch: ending the session there dropped
 *  the drive's own first list load and showed "the session ended" in it. Unknown (no answer echoed yet): the same
 *  person in the same tenant, so it is not treated as a switch either. P10 is not traded: the server resolved every
 *  request with the identity it had; this only decides whether the client drops answers (decision D3 in the #1316 PR). */
export function identityChanges(tenantId: string, prev: string | undefined, next: string | undefined): boolean {
  if (prev === next) return false
  if (prev === undefined && next !== undefined) {
    const acting = lastActingOrgEmail(tenantId)
    return acting !== undefined && acting !== next
  }
  return true
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { personalTenantId, user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // ── Tenant ID from URL ──────────────────────────────────────────────
  const value = useMemo(() => {
    const path = location.pathname
    const fallback = personalTenantId || ''

    if (path.startsWith('/~/') || path === '/~') {
      return { tenantId: fallback, isPersonal: true, tenantSlug: null }
    }

    const match = path.match(/^\/t\/([^/]+)/)
    if (match) {
      const slug = match[1]
      return { tenantId: slug, isPersonal: slug === fallback, tenantSlug: slug }
    }

    return { tenantId: fallback, isPersonal: true, tenantSlug: null }
  }, [location.pathname, personalTenantId])

  const { tenantId, isPersonal, tenantSlug } = value

  // ── Org email state (server-side, hydrated once on mount) ───────────
  // Map of tenantId → active email for the current session, fetched from
  // GET /api/auth/active-emails. NO localStorage involvement.
  const [orgEmailsByTenant, setOrgEmailsByTenant] = useState<Record<string, string>>({})
  const [orgEmailsHydrated, setOrgEmailsHydrated] = useState(false)
  const activeOrgEmail = orgEmailsByTenant[tenantId]

  // ── Refs for synchronous API header access ──────────────────────────
  const tenantRef = useRef(tenantId)
  tenantRef.current = tenantId

  const orgEmailRef = useRef<string | undefined>(activeOrgEmail)
  orgEmailRef.current = activeOrgEmail

  // Register getters synchronously during render, NOT in useEffect.
  //
  // React effect ordering is bottom-up — children fire their useEffects
  // BEFORE their parent's. If we register here from useEffect, a child
  // component that fires an API call from its mount effect runs first,
  // sees a stale/null getter, and the request goes out without
  // X-Tenant-Id. The server then defaults to personal tenant. Symptom:
  // workspace popover shows personal workspaces after a deep-link refresh
  // into an org workspace (issue #18).
  //
  // Registering during render guarantees the getter is wired before any
  // descendant can mount or fire an effect. The closures over `tenantRef`
  // / `orgEmailRef` keep the values up-to-date on every subsequent render
  // (refs are mutated above, synchronously each render).
  //
  // Side-effect-during-render is safe here because:
  //   1. The call is idempotent — passing the same closure shape every time.
  //   2. The closures don't capture render-tick state — they read refs.
  //   3. We accept React StrictMode's double-render: the second registration
  //      installs an equivalent function, no behaviour change.
  setActiveTenantGetter(() => tenantRef.current)
  setActiveOrgEmailGetter(() => orgEmailRef.current ?? null)

  // Hydrate orgEmail map from server on mount, on auth-user change, AND on
  // every tenant change. The tenantId dep covers the case where the server-
  // side selection was set out-of-band (deep link, second tab, e2e helper)
  // between the previous hydration and now — without it, the App-level
  // default-pick effect sees `activeOrgEmail=undefined` for the new tenant
  // and POSTs the user's primary email, clobbering the actual selection.
  //
  // Track completion via `orgEmailsHydrated` so the default-pick can wait.
  useEffect(() => {
    let cancelled = false
    setOrgEmailsHydrated(false)
    request<{ activeEmails: Record<string, string> }>('/api/auth/active-emails')
      .then(res => {
        if (cancelled) return
        setOrgEmailsByTenant(res.activeEmails || {})
        setOrgEmailsHydrated(true)
      })
      .catch(() => {
        // Unauthenticated or transient — still mark hydrated so the SPA can
        // fall back to its default-pick path; the user just isn't signed in
        // yet (or the request failed and the auth layer will redirect).
        if (!cancelled) setOrgEmailsHydrated(true)
      })
    return () => { cancelled = true }
  }, [personalTenantId, tenantId])

  // ── Pending switch gate ─────────────────────────────────────────────
  const [pendingSwitch, setPendingSwitch] = useState(false)

  // Clear pendingSwitch after React state has settled for the new tenant
  useEffect(() => {
    if (pendingSwitch) setPendingSwitch(false)
  }, [tenantId, pendingSwitch])

  const isReady = !pendingSwitch

  /** E7 PR 0b — the ONE place a switch of tenant / identity is applied to the client: only after the server CONFIRMED
   *  it (a refused switch never gets here, so the session — and its in-flight work — stays), the session ending
   *  synchronously first (when something actually changes), then the headers every request carries. A switch to the
   *  tenant already in view (another email — how AvatarMenu and the mobile nav switch identity) is an identity switch
   *  whatever the caller named it (#1316 — it bumps the session epoch). */
  const applySwitch = (targetTenantId: string, email: string | undefined, reason: 'tenant-switched' | 'identity-switched') => {
    if (tenantRef.current !== targetTenantId || identityChanges(targetTenantId, orgEmailRef.current, email)) endSession(tenantRef.current === targetTenantId ? 'identity-switched' : reason)
    // the navigation to the new tenant that follows is this switch's own move — not a second end
    if (tenantRef.current !== targetTenantId) sessionContextMoveAccountedFor(sessionContextOf('/', targetTenantId))
    tenantRef.current = targetTenantId
    orgEmailRef.current = email
  }
  // backstop: the route reaching another SESSION CONTEXT some other way — another tenant's drive, a share view ↔ the
  // drive, one share session ↔ another (a link, Back / Forward) — ends the session, once; a helper's own move is
  // accounted for. From NO context ('' — an anonymous visitor signing in) it is a first sign-in: nothing ends.
  const contextKey = sessionContextOf(location.pathname, tenantId)
  const sessionContextRef = useRef(contextKey)
  // a LAYOUT effect (#1316): every layout effect of the commit runs before any passive one, so the session has ended
  // before the new route's mount effects fire their first requests — a passive effect here ran AFTER theirs (children
  // first), so they captured the ending session and their answers were dropped (an error in the new view)
  useLayoutEffect(() => {
    sessionContextChanged(sessionContextRef.current, contextKey)
    sessionContextRef.current = contextKey
  }, [contextKey])

  // ── setActiveOrgEmail — POSTs to server, then updates React state ────
  const setActiveOrgEmail = useCallback(async (email: string | undefined) => {
    const tid = tenantRef.current
    if (email) {
      try {
        await request('/api/auth/active-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tenantId: tid, email }),
        })
      } catch {
        return  // server rejected — leave state unchanged
      }
    } else {
      try {
        await request(`/api/auth/active-email/${encodeURIComponent(tid)}`, { method: 'DELETE' })
      } catch {
        return  // the clear failed — the identity is unchanged, so is the session
      }
    }
    // The tab may have moved to another tenant meanwhile (a link): then this was a choice for `tid` only — recorded
    // below — never a switch of the tenant now in view (no session end, no header writes).
    if (tenantRef.current === tid) applySwitch(tid, email, 'identity-switched')
    setOrgEmailsByTenant(prev => {
      const next = { ...prev }
      if (email) next[tid] = email
      else delete next[tid]
      return next
    })
  }, [])

  // A choice made this page load stays made — across tenant switches — until
  // the page reloads or the ACCOUNT changes (#1253, below): a different account,
  // or the same one after signing out, has made no choice yet.
  const [tenantChosen, setTenantChosen] = useState(false)
  const markTenantChosen = useCallback(() => setTenantChosen(true), [])

  // #1253 — where this page load entered the app (`nextEntry`, lib/identityShell.ts).
  const [entry, dispatchEntry] = useReducer(nextEntry, undefined)
  // Read by the callbacks so the per-navigation calls that cannot change
  // anything (an entry already captured / already voided) dispatch nothing — a
  // dispatch from a layout effect re-renders this app-root provider in the
  // layout phase, even when the reducer returns the same state.
  const entryNow = useRef(entry)
  entryNow.current = entry
  const captureEntry = useCallback((path: string, shell: ShellKind) => {
    if (entryNow.current === undefined) dispatchEntry({ type: 'captured', path, shell })
  }, [])
  const markEntryUnrouted = useCallback((path: string, shell: ShellKind) => {
    // Only what the reducer would act on — the entry itself, caught by the shell
    // that captured it. `undefined` may be a capture dispatched in this very
    // commit, not yet applied (the catch-all runs right after EntryCapture):
    // let the reducer judge that one.
    const e = entryNow.current
    if (e === undefined || (e.path === path && e.shell === shell)) dispatchEntry({ type: 'unrouted', path, shell })
  }, [])
  const entryPath = entry ? entry.path : location.pathname
  // The ONE reset point for both "a choice was made" facts: the RENDERED account
  // changing (a layout effect — at commit, before paint). A → null → A within one
  // render is no change; the first sign-in (null → A) resets nothing.
  const renderedUserId = useRef<string | null>(null)
  useLayoutEffect(() => {
    const to = user?.id ?? null
    const from = renderedUserId.current
    if (from === to) return
    renderedUserId.current = to
    dispatchEntry({ type: 'account', from, to })
    if (from !== null) setTenantChosen(false)
  }, [user?.id])

  // ── switchTenant (atomic: refs first, then navigate) ────────────────
  const chooseIdentityIfUnset = useCallback(async (targetId: string, email: string): Promise<boolean> => {
    try {
      await request('/api/auth/active-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: targetId, email, ifUnset: true }),
      })
      return true
    } catch {
      return false
    }
  }, [])

  const switchTenant = useCallback(async (targetId: string, orgEmail?: string, opts?: { to?: string }) => {
    // 1. Persist active email to server BEFORE navigation so the next set of
    //    API calls (which fire as soon as the new tenant page mounts) see the
    //    correct identity.
    if (orgEmail) {
      try {
        await request('/api/auth/active-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tenantId: targetId, email: orgEmail }),
        })
      } catch {
        // If the server refuses (e.g. no membership), abort the switch.
        return
      }
    }

    // 2–4 run behind the identity portal (IdentitySwitchCurtain): the new identity's colour grows out of the
    // avatar, the switch happens under it, and it collapses back into the avatar. Unchanged identity: no portal.
    const sameIdentity = targetId === tenantRef.current && (orgEmail ?? null) === (orgEmailRef.current ?? null)
    await playIdentitySwitch(sameIdentity ? null : identityCardFor(targetId, orgEmail), () => {
      // 2. Update refs synchronously — API calls will use correct headers immediately (the session ends first)
      applySwitch(targetId, orgEmail, 'tenant-switched')

      // 3. Update React state
      setOrgEmailsByTenant(prev => {
        const next = { ...prev }
        if (orgEmail) next[targetId] = orgEmail
        else delete next[targetId]
        return next
      })
      setPendingSwitch(true)

      // Reached only on success (a refused active-email POST returned above), so
      // the picker treats a failed switch as no choice and stays put — while any
      // successful switch, INCLUDING one back to Personal, dismisses it. This is
      // the one line that makes `tenantChosen` correct for every surface at once.
      setTenantChosen(true)

      // 4. Navigate — URL change triggers TenantProvider re-render. A switch
      //    lands on this deployment's HOME surface, which is `chat` for Papan and
      //    the app launcher for a launcher deployment. Hardcoding `/chat` here
      //    would drop a launcher user onto a surface their deployment does not
      //    use, every time they changed org.
      const isTargetPersonal = targetId === personalTenantId
      navigate(opts?.to ?? landingPath(isTargetPersonal ? '/~' : `/t/${targetId}`))
    })
  }, [personalTenantId, navigate])

  return (
    <TenantContext.Provider value={{
      tenantId,
      isPersonal,
      tenantSlug,
      activeOrgEmail,
      setActiveOrgEmail,
      switchTenant,
      chooseIdentityIfUnset,
      isReady,
      orgEmailsHydrated,
      tenantChosen,
      markTenantChosen,
      entryPath,
      captureEntry,
      markEntryUnrouted,
    }}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext)
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}
